/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

/**
 * Čistič místa na disku.
 * Snaží se zajistit dostatek volného místa pro nový obsah.
 * @implements {EventBus}
 */
class Cleaner {
  /**
   * @param {AssetDownloader} assetDownloader
   * @param {Store} primaryStore Hlavní datový sklad ve kterém je uložen přehrávaný obsah.
   * @param {Session} session Sezení s nastavením zařízení.
   */
  constructor(assetDownloader, primaryStore, session) {
    this.assetDownloader = assetDownloader;
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.primaryStore = primaryStore;
    this.session = session;
  }

  /**
   * Vyčistí místo na disku pro nový obsah.
   * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
   * @returns {Promise.<CleanerInfo>} Informace o vyčištěných datových modelech.
   */
  clean(contentInfo) {
    const { primaryStore } = this;
    // Všechna aktuálně přítomná média se rozdělí dle k nim přiřazeným assetům.
    const mediumArrayByAssetId = primaryStore
      .filter('medium')
      .reduce((acc, medium) => {
        const { assetId } = medium;
        if (!isNil(assetId)) {
          acc[assetId] = acc[assetId] || [];
          acc[assetId].push(medium);
        }
        return acc;
      }, {});
    // Předpočítají se identifikátory médií v novém obsahu pro rychlejší vyhledávání.
    const contentMediumIdArray = contentInfo.mediumArray.map(
      (medium) => medium.id
    );
    // K aktuálně přítomným assetům se přidá seznam médií, které na ně mají vazbu.
    // Pokud je nějaké médium v novém obsahu, tak se asset odfiltruje pryč.
    const cleanerGroupArray = primaryStore
      .filter('asset')
      .map((asset) => ({
        asset,
        mediumArray: mediumArrayByAssetId[asset.id] || []
      }))
      .filter(
        (cleanerGroup) =>
          !cleanerGroup.mediumArray.some((medium) =>
            contentMediumIdArray.includes(medium.id)
          )
      );
    // Pokud se bude nějaký asset mazat, tak se napřed zastaví obsah.
    if (cleanerGroupArray.length) {
      // Požadavek na zastavení aktuálně přehrávaného obsahu.
      this.emitter.emit('content', {
        contentTasks: [],
        firmwareRelease: null,
        layerArray: [],
        layoutArray: [],
        mediumArray: [],
        playlistArray: [],
        powerTasks: [],
        // Zachová se obsah aktuální session.
        // Při zrušení hodnot by mohlo dojít ke zbytečné změně rotace obrazovky apod.
        session: this.session,
        store: primaryStore,
        taskArray: [],
        timerArray: [],
        trackArray: []
      });
    }
    return cleanerGroupArray.reduce(
      (acc, cleanerGroup) =>
        acc.then((cleanerInfo) =>
          // Zruší se vazby médií na odstraňovaný asset.
          cleanerGroup.mediumArray
            .reduce(
              (updateAcc, medium) =>
                updateAcc.then(() =>
                  primaryStore
                    .update('medium', medium.id, { assetId: null })
                    .then(() => {
                      // Úspěšně aktualizované médium se přidá do výstupu.
                      cleanerInfo.mediumArray.push(medium);
                    })
                ),
              Promise.resolve()
            )
            // Odebere se asset.
            .then(() => {
              const assetId = cleanerGroup.asset.id;
              return this.assetDownloader.destroy(assetId);
            })
            // Úspěšně odebraný asset se přidá do výstupu.
            .then(() => {
              cleanerInfo.assetArray.push(cleanerGroup.asset);
            })
            // Pokud něco selže, tak se přeskočí na další asset.
            .catch((error) => {
              this.emitter.emit('error', error);
            })
            // Předání akumulovaných výsledků pro další asset.
            .then(() => cleanerInfo)
        ),
      Promise.resolve({ assetArray: [], mediumArray: [] })
    );
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }
}

/**
 * Nový obsah zařízení k zobrazení.
 * Používá se pro přerušení přehrávání aktuálního obsahu, aby mohl být vyčištěn.
 * @event Cleaner#content
 * @param {ContentInfo} contentInfo Informace o novém obsahu zařízení.
 */

/**
 * Chybová událost.
 * @event Cleaner#error
 * @param {Error} error Chybové hlášení.
 */

export default Cleaner;
